import usePermissions from "../../hooks/usePermissions";
import { FEATURES } from "../../hooks/usePermissions/permissions";
import { organizationTypeToName, userRoleToName } from "../../lib/utils";
import Button from "../../components/Button";
import {
  SharingInvitationStatus,
  SharingInvitationType,
} from "../../__generated__/graphql";
import { motion } from "motion/react";
import UpdateOrganizationModal from "./components/UpdateOrganizationModal";
import { useState } from "react";
import UpdateUserModal from "./components/UpdateUserModal";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import { FaPen, FaPlus } from "react-icons/fa6";
import Avatar from "../../components/Avatar";
import useOrganizationMembers from "../../hooks/useOrganizationMembers";
import { format } from "date-fns";
import CreateSharingInvitationModal from "../../components/CreateSharingInvitationModal";
import { useAuth } from "../../hooks/useAuth";
import useCurrentOrgInvitations from "../collaborators/components/InvitationsList/hooks/useCurrentOrgInvitations";
import PimsIntegration from "./components/PimsIntegration";

export default function Account() {
  const { currentOrganization, userRole, userOrganization, userInfos } =
    useUser();
  const { userId } = useAuth();

  const { can } = usePermissions();

  const [isEditingOrganization, setIsEditingOrganization] = useState(false);
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const { data: organizationMembers } = useOrganizationMembers();
  const { data: currentOrgInvitations } = useCurrentOrgInvitations();
  // const [deleteUserOrganization] = useMutation(DeleteUserOrganizationDocument);

  // const handleDeleteMember = async (memberId: string) => {
  //   const confirmed = confirm("Voulez-vous vraiment supprimer ce membre ?");

  //   if (!confirmed) {
  //     return;
  //   }

  //   try {
  //     await deleteUserOrganization({
  //       variables: { userOrganizationId: memberId },
  //       refetchQueries: [OrganizationMembersDocument],
  //     });

  //     toast.success("Membre supprimé avec succès");
  //   } catch {
  //     toast.error("Une erreur est survenue lors de la suppression du membre");
  //   }
  // };

  const userInfosSection = () => {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 bg-dark p-4 rounded-md border border-border w-xl">
          <div className="flex justify-between items-center">
            <div className="text-xl font-bold">Mes informations</div>
            <FaPen
              className="w-4 h-4 cursor-pointer hover:text-secondary"
              onClick={() => setIsEditingUser(true)}
            />
          </div>

          <div className="flex gap-4">
            <div
              onClick={() => setIsEditingUser(true)}
              className="cursor-pointer"
            >
              <Avatar
                shape="square"
                size="xlarge"
                userOrganization={userOrganization}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <p className="text-sm text-primary font-bold">Email:</p>
                <div className="text-sm text-secondary">{userInfos?.email}</div>
              </div>

              <div className="flex gap-2">
                <p className="text-sm text-primary font-bold">Prénom:</p>
                <div className="text-sm text-secondary">
                  {userInfos?.firstName}
                </div>
              </div>

              <div className="flex gap-2">
                <p className="text-sm text-primary font-bold">Nom:</p>
                <div className="text-sm text-secondary">
                  {userInfos?.lastName}
                </div>
              </div>

              <div className="flex gap-2">
                <p className="text-sm text-primary font-bold">Téléphone:</p>
                <div className="text-sm text-secondary">
                  {userInfos?.phoneNumber ?? "Non renseigné"}
                </div>
              </div>

              <div className="flex gap-2">
                <p className="text-sm text-primary font-bold">Fonction:</p>
                <div className="text-sm text-secondary">
                  {userInfos?.jobTitle ?? "Non renseigné"}
                </div>
              </div>

              <div className="flex gap-2">
                <p className="text-sm text-primary font-bold">
                  Date de naissance:
                </p>
                <div className="text-sm text-secondary">
                  {userInfos?.birthDate
                    ? format(userInfos?.birthDate, "dd/MM/yyyy")
                    : "Non renseigné"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const organizationSection = () => {
    return (
      <div className="flex flex-col gap-2 bg-dark p-4 rounded-md border border-border w-xl">
        <div className="flex justify-between items-center">
          <div className="text-xl font-bold">Organisation</div>
          <FaPen
            className="w-4 h-4 cursor-pointer hover:text-secondary"
            onClick={() => setIsEditingOrganization(true)}
          />
        </div>

        {currentOrganization?.name ? (
          <div className="flex gap-2">
            <p className="text-sm text-primary font-bold">
              Nom de l'organisation:
            </p>
            <div className="text-sm text-secondary">
              {currentOrganization?.name}
            </div>
          </div>
        ) : null}

        {currentOrganization?.organizationType ? (
          <div className="flex gap-2">
            <p className="text-sm text-primary font-bold">
              Type d'organisation :
            </p>
            <div className="text-sm text-secondary">
              {organizationTypeToName[currentOrganization?.organizationType]}
            </div>
          </div>
        ) : null}

        {userRole ? (
          <div className="flex gap-2">
            <p className="text-sm text-primary font-bold">
              Rôle dans l'organisation :
            </p>
            <div className="text-sm text-secondary">
              {userRoleToName[userRole]}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const membersSection = () => {
    const pendingMembersInvitations =
      currentOrgInvitations?.sharingInvitations?.filter(
        (invitation) =>
          invitation?.status === SharingInvitationStatus.Pending &&
          invitation?.invitationType ===
            SharingInvitationType.OrganizationMember
      );

    return (
      <div className="flex flex-col gap-2 bg-dark p-4 rounded-md border border-border w-xl">
        <div className="flex justify-between items-center">
          <div className="text-xl font-bold">Membres</div>
          <div className="flex justify-end">
            {can(FEATURES.INVITE_MEMBER) ? (
              <Button
                variant="secondary"
                className="w-fit flex items-center gap-2"
                onClick={() => setIsInvitingMember(true)}
                dataTestId="invite-member-button"
              >
                <FaPlus className="w-4 h-4" />
                Inviter un membre
              </Button>
            ) : null}
          </div>
        </div>

        {!organizationMembers ||
        organizationMembers.organizationMembers.length === 1 ? (
          <div className="text-sm text-secondary">
            Vous n'avez pas de membres dans votre organisation.
          </div>
        ) : null}

        {pendingMembersInvitations?.length &&
        pendingMembersInvitations.length > 0
          ? pendingMembersInvitations.map((invitation) => {
              if (!invitation) return null;

              return (
                <div
                  key={invitation.id}
                  className="flex justify-between gap-2 bg-card rounded-md border border-border items-center p-2"
                >
                  <div className="flex gap-2 items-center">
                    <div className="flex flex-col ">
                      <div className="text-sm text-secondary">En attente</div>
                      <div className="flex gap-2 items-center">
                        <p className="text-sm text-primary font-bold">
                          {invitation.recipientEmail}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          : null}

        {organizationMembers?.organizationMembers
          .slice()
          .sort((a, b) => {
            if (!a || !b) return 0;
            // Current user should always be first
            if (a.userId === userId) return -1;
            if (b.userId === userId) return 1;
            return 0;
          })
          .map((member) => {
            if (!member) {
              return null;
            }

            return (
              <div
                key={member.id}
                className="flex justify-between gap-2 bg-card rounded-md border border-border items-center p-2"
              >
                <div className="flex gap-2 items-center">
                  <Avatar
                    shape="square"
                    size="medium"
                    userOrganization={member}
                  />
                  <div className="flex flex-col ">
                    <div className="flex gap-2 items-center">
                      {member.userId === userId ? (
                        <span className="text-xs text-secondary">(moi)</span>
                      ) : null}

                      <p className="text-sm text-primary font-bold">
                        {member.name}
                      </p>
                      {member.jobTitle ? (
                        <p className="text-sm text-secondary">
                          {member.jobTitle}
                        </p>
                      ) : null}
                    </div>

                    <p className="text-sm text-accent">
                      {userRoleToName[member.role]}
                    </p>
                  </div>
                </div>

                {/* TODO: Fix delete member feature */}
                {/* {can(FEATURES.DELETE_MEMBER) && member.userId !== userId ? (
                  <XMarkIcon
                    className="w-4 h-4 cursor-pointer hover:text-secondary"
                    onClick={() => handleDeleteMember(member.id)}
                  />
                ) : null} */}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <>
      <CreateSharingInvitationModal
        isModalOpen={isInvitingMember}
        onClose={() => setIsInvitingMember(false)}
        invitationType={SharingInvitationType.OrganizationMember}
      />

      <UpdateUserModal
        isOpen={isEditingUser}
        onClose={() => setIsEditingUser(false)}
      />

      <UpdateOrganizationModal
        isOpen={isEditingOrganization}
        onClose={() => setIsEditingOrganization(false)}
      />

      <motion.div
        className="flex flex-col gap-2 text-primary"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="account-page"
      >
        <div className="text-2xl font-bold">Compte</div>

        <div className="flex gap-2">
          <div className="flex flex-col gap-2">
            {userInfosSection()}
            {organizationSection()}
            {membersSection()}
            <PimsIntegration />
          </div>
        </div>
      </motion.div>
    </>
  );
}
